'use client'

import { useState, useEffect } from 'react'
import type { Product } from '../types'
import { X, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { ModalLoadingSkeleton } from './TableLoadingSkeleton'
import { getListOfFunnels } from '../../../services/generativeAI/generativeAIAxios/generateCopysAxios'
import { CreateExperimentPayload, postCreateExperiment, postBulkUpsertExperiments } from '../../../services/experiments/experimentsService'
import toast from 'react-hot-toast'
import { dictionary } from '@/utils/dictionary'
import { formatCurrency, getCurrencySymbol } from '@/utils/formatters'
import { useAuth } from '@/context/AuthContext'
import { ConfirmCreateAbTestModal } from './ConfirmationModal'

interface CreateTestModalProps {
  isOpen: boolean
  onClose: () => void
}

interface VariantInput {
  name: string
  price: string
  traffic_percentage: string
}

export function CreateTestModal({ isOpen, onClose }: CreateTestModalProps) {
  const { language, country } = useAuth()
  const currencySymbol = getCurrencySymbol(country)
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [testName, setTestName] = useState('')
  const [variants, setVariants] = useState<VariantInput[]>([
    { name: 'A', price: '', traffic_percentage: '50' },
    { name: 'B', price: '', traffic_percentage: '50' },
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  useEffect(() => {
    const getProducts = async () => {
      if (isOpen && step === 1) {
        const funnels = await getListOfFunnels()
        const funnelsFiltered = funnels.map((funnel: any) => {
          return {
            id: funnel.id,
            name: funnel.brand_name,
            product_id: funnel.product.id,
            description: funnel.product.description,
            price: funnel.product.price,
            image: funnel.product.image
          }
        })

        setProducts(funnelsFiltered)
        setIsLoading(false)
      }
    }
    getProducts()
  }, [isOpen, step])

  useEffect(() => {
    if (!isOpen) {
      setStep(1)
      setSelectedProduct(null)
      setTestName('')
      setVariants([
        { name: 'A', price: '', traffic_percentage: '50' },
        { name: 'B', price: '', traffic_percentage: '50' },
      ])
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedProduct) {
      setTestName(`Test de precios - ${selectedProduct.name}`)
      setVariants(prev => prev.map((v, i) => ({
        ...v,
        price: i === 0 ? selectedProduct.price.toString() : v.price
      })))
      setVariants(prev => prev.map((v, i) => ({
        ...v,
        price: i === 1 ? (selectedProduct.price * 1.15).toString() : v.price
      })))
    }
  }, [selectedProduct])


  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product)
  }

  const handleNextStep = () => {
    if (selectedProduct) {
      setStep(2)
    }
  }

  const handleAddVariant = () => {
    if (variants.length >= 5) {
      toast.error(dictionary['maximum-variants-reached'][language])
      return
    }

    const nextLetter = String.fromCharCode(65 + variants.length)
    const newTraffic = Math.floor(100 / (variants.length + 1))

    const updatedVariants = variants.map(v => ({
      ...v,
      traffic_percentage: newTraffic.toString()
    }))

    const remaining = 100 - (newTraffic * variants.length)
    updatedVariants.push({
      name: nextLetter,
      price: '',
      traffic_percentage: remaining.toString()
    })

    setVariants(updatedVariants)
  }

  const handleRemoveVariant = (index: number) => {
    if (variants.length <= 2) return

    const newVariants = variants.filter((_, i) => i !== index)
    const newTraffic = Math.floor(100 / newVariants.length)
    const redistributed = newVariants.map((v, i) => ({
      ...v,
      name: String.fromCharCode(65 + i),
      traffic_percentage: i === newVariants.length - 1
        ? (100 - newTraffic * (newVariants.length - 1)).toString()
        : newTraffic.toString()
    }))

    setVariants(redistributed)
  }

  const handleVariantChange = (index: number, field: keyof VariantInput, value: string) => {
    setVariants(prev => prev.map((v, i) =>
      i === index ? { ...v, [field]: value } : v
    ))
  }

  const validatePercentage = (percentages: string[]) => {
    const totalPercentage = percentages.reduce((acc, curr) => acc + parseInt(curr), 0)
    if (totalPercentage !== 100) {
      return false
    }
    return true
  }

  const handleOpenConfirmationModal = () => {
    if (!validatePercentage(variants.map(v => v.traffic_percentage))) {
      toast.error(dictionary['percentage-error'][language])
      return
    }
    setIsConfirmationModalOpen(true)
  }

  const handleConfirmCreateAbTest = () => {
    postBulkUpsertExperiments(selectedProduct?.product_id.toString() || '')
      .then(() => {
        setIsConfirmationModalOpen(false)
        handleCreate()
      })
      .catch(() => {
        toast.error(dictionary['error-upserting-experiments'][language])
      })
  }

  const handleCreate = () => {
    if (!selectedProduct || !testName) return

    const data: CreateExperimentPayload = {
      website_id: selectedProduct.id.toString(),
      product_id: selectedProduct.product_id.toString(),
      base_price: selectedProduct.price,
      name: testName,
      variants: variants.map(v => ({
        name: v.name,
        price: parseFloat(v.price) || 0,
        traffic_percentage: parseInt(v.traffic_percentage) || 0
      }))
    }
    postCreateExperiment(data)
      .then(() => {
        toast.success(dictionary['test-created-successfully'][language])
        onClose()
        window.location.reload()
      })
      .catch(() => {
        toast.error(dictionary['error-creating-test'][language])
      })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {step === 1 ? dictionary['select-product'][language] : dictionary['configure-prices'][language]}
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <span className={`px-3 py-1 rounded-full ${step === 1 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {dictionary['step-1'][language]}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={`px-3 py-1 rounded-full ${step === 2 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {dictionary['step-2'][language]}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {step === 1 ? (
            <>
              <p className="text-sm text-gray-600 mb-5">
                {dictionary['select-product-description'][language]}
              </p>

              {isLoading ? (
                <ModalLoadingSkeleton />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`flex gap-3 p-4 rounded-xl border-2 text-left transition-all ${selectedProduct?.id === product.id
                        ? 'border-blue-500 bg-blue-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <img
                        src={product.image || "/placeholder.svg"}
                        alt={product.name}
                        className="w-16 h-16 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {product.name}
                          </h3>
                          <span className="text-xs text-gray-400 flex-shrink-0 truncate max-w-20">#{product.id}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                        <p className="text-sm font-semibold text-gray-900 mt-2">
                          {dictionary['current-price'][language]}: {formatCurrency(product.price, currencySymbol)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedProduct?.image || "/placeholder.svg"}
                    alt={selectedProduct?.name}
                    className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{selectedProduct?.name}</p>
                    <p className="text-sm text-gray-500">
                      {dictionary['base-price'][language]}: {formatCurrency(selectedProduct?.price || 0, currencySymbol)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {dictionary['change'][language]}
                </button>
              </div>

              {/* Test Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {dictionary['test-name'][language]}
                </label>
                <Input
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder={dictionary['test-name-placeholder'][language]}
                  className="w-full"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    {dictionary['price-variants'][language]} ({variants.length}/5)
                  </label>

                  <button
                    onClick={handleAddVariant}
                    disabled={variants.length >= 5}
                    className={`
                      flex items-center gap-1 text-sm font-medium
                      ${variants.length >= 5
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700'
                      }
                    `}
                  >
                    <Plus className="w-4 h-4" />
                    {dictionary['add-variant'][language]}
                  </button>
                </div>

                <div className="space-y-4">
                  {variants.map((variant, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{dictionary['variant'][language]} {variant.name}</h4>
                        {variants.length > 2 && (
                          <button
                            onClick={() => handleRemoveVariant(index)}
                            className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{dictionary['price'][language]}</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currencySymbol}</span>
                            <Input
                              type="number"
                              value={variant.price}
                              onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                              placeholder="0.00"
                              className="pl-7"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">% de Tráfico</label>
                          <div className="relative">
                            <Input
                              type="number"
                              value={variant.traffic_percentage}
                              onChange={(e) => handleVariantChange(index, 'traffic_percentage', e.target.value)}
                              placeholder="33"
                              className="pr-7"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-700">
                <p className="font-medium mb-2">{dictionary['information'][language]}</p>
                <div className="text-xs">
                  <p className="mb-1 ml-2">{dictionary['visitors-will-be-automatically-divided-between-variants'][language]}</p>
                  <p className="mb-1 ml-2">{dictionary['each-variant-will-display-a-different-price'][language]}</p>
                  <p className="mb-1 ml-2">{dictionary['conversions-and-metrics-will-be-recorded-for-each-price'][language]}</p>
                  <p className="mb-1 ml-2">{dictionary['you-can-pause-or-end-the-test-at-any-time'][language]}</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          {step === 2 ? (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="gap-2"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              {dictionary['back'][language]}
            </Button>
          ) : (
            <div />
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {dictionary['cancel'][language]}
            </Button>
            {step === 1 ? (
              <Button
                onClick={handleNextStep}
                disabled={!selectedProduct}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {dictionary['next'][language]}
              </Button>
            ) : (
              <Button
                onClick={handleOpenConfirmationModal}
                disabled={!testName || variants.some(v => !v.price)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {dictionary['create-ab-test'][language]}
              </Button>
            )}
          </div>
        </div>
      </div>
      <ConfirmCreateAbTestModal
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleConfirmCreateAbTest}
        loading={isLoading}
      />
    </div>
  )
}
